import { useRef, useState, type TouchEvent } from 'react';

/** Past this, releasing commits the move. Also the point the hint reaches full strength. */
const COMMIT_DISTANCE = 70;
/** Movement before we decide whether this is a horizontal swipe or a scroll. */
const AXIS_LOCK = 10;
/** Furthest the view will travel, however hard you pull. */
const MAX_PULL = 28;
/** A blocked direction barely gives at all. */
const MAX_PULL_BLOCKED = 10;

/**
 * Asymptotic resistance: the view follows closely at first and then stiffens,
 * approaching the limit without ever reaching it. Moving the whole page one
 * to one felt loose and made the page look like it was falling over; this
 * reads as tension against a stop instead.
 */
function damp(distance: number, limit: number): number {
  return Math.sign(distance) * limit * (1 - Math.exp(-Math.abs(distance) / limit));
}

export type SwipeDirection = 'left' | 'right' | null;

export interface SwipeState {
  /** Current horizontal displacement in px, already damped. */
  offset: number;
  /** True while a finger is down and moving horizontally. */
  dragging: boolean;
  /** Which way the finger is heading, once the axis has locked. */
  direction: SwipeDirection;
  /** 0 to 1: how close this drag is to committing. */
  progress: number;
  /** Whether releasing now would actually navigate. */
  willCommit: boolean;
}

/**
 * Horizontal swipe with live feedback.
 *
 * The axis locks after a few pixels so a vertical scroll is never hijacked --
 * the day view is a long list, and a swipe that fired on an angled flick would
 * change the date while you were reading it. A direction that cannot be
 * travelled still moves, but only slightly, so the gesture registers as
 * refused rather than ignored.
 */
const AT_REST: SwipeState = {
  offset: 0,
  dragging: false,
  direction: null,
  progress: 0,
  willCommit: false,
};

export function useSwipe(handlers: {
  onLeft?: () => void;
  onRight?: () => void;
  canLeft?: boolean;
  canRight?: boolean;
  /** Off while something else owns the gesture, such as dragging an entry. */
  enabled?: boolean;
}) {
  const { canLeft = true, canRight = true, enabled = true } = handlers;
  const start = useRef<{ x: number; y: number } | null>(null);
  const axis = useRef<'undecided' | 'horizontal' | 'vertical'>('undecided');
  const [state, setState] = useState<SwipeState>(AT_REST);

  const reset = () => {
    const wasDragging = axis.current === 'horizontal';
    axis.current = 'undecided';
    start.current = null;
    // Only touch state if a horizontal drag actually moved something. A plain
    // vertical scroll ends with a touchend too, and re-rendering the whole day
    // on the lift of every flick is a hitch you feel at the end of each one.
    if (!wasDragging) return;
    setState(AT_REST);
  };

  return {
    swipe: state,
    handlers: {
      onTouchStart: (e: TouchEvent) => {
        const touch = e.touches[0];
        if (!enabled || !touch) return;
        start.current = { x: touch.clientX, y: touch.clientY };
        axis.current = 'undecided';
      },

      onTouchMove: (e: TouchEvent) => {
        const from = start.current;
        const touch = e.touches[0];
        if (!from || !touch) return;

        const dx = touch.clientX - from.x;
        const dy = touch.clientY - from.y;

        if (axis.current === 'undecided') {
          if (Math.abs(dx) < AXIS_LOCK && Math.abs(dy) < AXIS_LOCK) return;
          axis.current = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
        }
        if (axis.current !== 'horizontal') return;

        // Swiping left moves forward in time, matching the arrow on that side.
        const blocked = dx < 0 ? !canLeft : !canRight;
        const offset = damp(dx, blocked ? MAX_PULL_BLOCKED : MAX_PULL);
        // Progress tracks the finger, not the damped offset, so the glow keeps
        // responding after the view has stopped moving.
        const progress = blocked ? 0 : Math.min(Math.abs(dx) / COMMIT_DISTANCE, 1);

        setState({
          offset,
          dragging: true,
          direction: dx < 0 ? 'left' : 'right',
          progress,
          willCommit: !blocked && Math.abs(dx) >= COMMIT_DISTANCE,
        });
      },

      onTouchEnd: () => {
        const { willCommit, direction } = state;
        reset();
        if (!willCommit) return;
        if (direction === 'left') handlers.onLeft?.();
        else if (direction === 'right') handlers.onRight?.();
      },

      onTouchCancel: reset,
    },
  };
}
